import React, { useState } from 'react';

import { Alert, Button, Flex, Grid, Group, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck, IconX } from '@tabler/icons-react';

import { useRunSchedulerMutation } from '@/store/slices/api/appointmentApiSlice';
import {
  CreateAppointmentResponse,
  useCreateAppointmentMutation
} from '@/store/slices/api/schedules/schedulesApiSlice';
import { appointmentApiType, appointmentCreateBody } from '@/utils/appointmentFrequency';
import { appointmentTypeLabel } from '@/utils/appointmentTypeLabel';
import { getTomorrow } from '@/utils/constants';
import formatDate from '@/utils/formatDate';
import DatePicker from '@/components/DatePickers/DatePicker';

import Dropdown from '@/ui/Dropdown';

import styles from '../DeviceScheduleModal.module.scss';
import SchedulesSummary from '../SchedulesSummary';
import { EditScheduleStepProps } from '../types';
import { APPOINTMENT_SETTINGS_ERROR, useAppointmentFrequency } from '../useAppointmentFrequency';

function schedulerSummaryLines(schedule: Record<string, string[]> | undefined): string[] {
  if (!schedule) return [];
  return Object.entries(schedule).flatMap(([type, dates]) =>
    (dates ?? []).map((value) => {
      const day = value.slice(0, 10);
      const [year, month, date] = day.split('-');
      const shown = year && month && date ? `${month}/${date}/${year}` : value;
      return `${type} - ${shown}`;
    })
  );
}

function schedulerErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'data' in error) {
    const data = error.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object' && 'message' in data) {
      const message = (data as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) return message;
      if (Array.isArray(message)) return message.map(String).join(' ');
    }
  }
  return 'Failed to update the schedule';
}

const BillableSchedule: React.FC<EditScheduleStepProps> = ({
  onBack,
  onClose,
  onSuccess,
  loading,
  isAppointmentManagedInRs,
  setLoading,
  patientSFId,
  deviceInfo
}) => {
  const [showSummary, setShowSummary] = useState(false);
  const [apiResponse, setApiResponse] = useState<CreateAppointmentResponse | null>(null);
  const [scheduledDate, setScheduledDate] = useState<Date | null>(null);
  const [appointmentType, setAppointmentType] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [scheduleStarted, setScheduleStarted] = useState(false);

  const [createAppointment] = useCreateAppointmentMutation();
  const [runScheduler] = useRunSchedulerMutation();
  const {
    ready: frequencyReady,
    failed: frequencyFailed,
    frequencyFor
  } = useAppointmentFrequency(patientSFId);
  const settingsError = frequencyFailed ? APPOINTMENT_SETTINGS_ERROR : null;

  const runDeviceSchedule = async () => {
    const deviceId = deviceInfo?.deviceId;
    if (!deviceId) return;
    setScheduleStarted(true);
    setAppointmentType('routine');
    setLoading(true);
    setErrorMessage(null);
    try {
      const result = await runScheduler({ deviceId }).unwrap();
      const nextDates = schedulerSummaryLines(result.schedule);
      const messages = (result.messages ?? []).filter(Boolean).join(' ');
      if (nextDates.length === 0) {
        setErrorMessage(messages || 'The scheduler did not change any dates.');
        return;
      }
      setApiResponse({
        success: true,
        message: messages,
        summary: { nextDates }
      });
      setShowSummary(true);
    } catch (error) {
      console.error('Error running the scheduler:', error);
      setErrorMessage(schedulerErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleManualSave = async () => {
    if (
      !appointmentType ||
      !scheduledDate ||
      !patientSFId ||
      !deviceInfo ||
      !frequencyReady ||
      frequencyFailed
    ) {
      return;
    }

    setLoading(true);
    setErrorMessage(null);

    try {
      const formattedDate = formatDate(scheduledDate, 'YYYY-MM-DD');

      const type = appointmentApiType(appointmentType);
      if (!type) return;
      const appointmentData = appointmentCreateBody({
        patientId: patientSFId,
        scheduledDate: formattedDate,
        type,
        frequency: frequencyFor(type, deviceInfo.type),
        modelNumber: deviceInfo.modelNumber,
        serialNumber: deviceInfo.serialNumber,
        deviceId: deviceInfo.deviceId
      });

      const result = await createAppointment(appointmentData).unwrap();

      if (!result.success) {
        console.error('API returned success: false', result);
        const errMsg =
          result.message || `Failed to create ${appointmentTypeLabel(appointmentType)} appointment`;
        setErrorMessage(errMsg);
        notifications.show({
          title: 'Error',
          message: errMsg,
          color: 'red',
          icon: <IconX size="1.1rem" />,
          autoClose: 5000
        });
        return;
      }

      notifications.show({
        title: 'Success',
        message:
          result.message ||
          `${appointmentTypeLabel(appointmentType)} appointment created successfully`,
        color: 'green',
        icon: <IconCheck size="1.1rem" />,
        autoClose: 3000
      });

      setApiResponse(result);
      setShowSummary(true);
    } catch (error) {
      console.error('Error creating billable appointment:', error);

      let errMsg = `Failed to create ${appointmentTypeLabel(appointmentType)} appointment`;

      if (error && typeof error === 'object' && 'data' in error) {
        const errorData = error.data as CreateAppointmentResponse;
        if (errorData && !errorData.success && errorData.message) {
          errMsg = errorData.message;
        }
      }

      setErrorMessage(errMsg);
      notifications.show({
        title: 'Error',
        message: errMsg,
        color: 'red',
        icon: <IconX size="1.1rem" />,
        autoClose: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowSummary(false);
    setApiResponse(null);
    setScheduledDate(null);
    setAppointmentType(null);
    setErrorMessage(null);

    if (apiResponse?.success) {
      onSuccess?.();
    }

    onClose();
  };

  if (!isAppointmentManagedInRs) {
    if (showSummary) {
      return (
        <SchedulesSummary
          appointmentType="Routine"
          nextDates={apiResponse?.summary?.nextDates}
          onClose={handleClose}
        />
      );
    }

    if (!scheduleStarted) {
      return (
        <Stack gap="xs">
          <Stack gap="xs" px={20} pt={16}>
            <Title order={5} fw={600} c="plum.9">
              Edit Schedule
            </Title>
            <Text size="sm" c="plum.8">
              This runs now and can move every date for this device. Back leaves the current dates
              in place.
            </Text>
          </Stack>
          <Group className={styles.buttonGroup} justify="flex-end">
            <Button
              variant="outline"
              color="plum.9"
              onClick={onBack}
              className={styles.modalButton}>
              Back
            </Button>
            <Button
              color="plum.9"
              onClick={() => void runDeviceSchedule()}
              className={styles.modalButton}>
              Run schedule
            </Button>
          </Group>
        </Stack>
      );
    }

    return (
      <Stack gap="md" px={20} pt={16} align="center" justify="center" style={{ minHeight: 200 }}>
        {errorMessage ? (
          <>
            <Alert icon={<IconAlertCircle size="1rem" />} title="Error" color="red" w="100%">
              {errorMessage}
            </Alert>
            <Button onClick={handleClose} color="plum.9">
              Close
            </Button>
          </>
        ) : (
          <Text size="sm" c="plum.7" fw={500}>
            Updating the schedule...
          </Text>
        )}
      </Stack>
    );
  }

  // Manual flow (when managed in RS) - show summary if available
  if (showSummary && appointmentType) {
    return (
      <SchedulesSummary
        appointmentType={appointmentTypeLabel(appointmentType)}
        nextDates={apiResponse?.summary?.nextDates}
        onClose={handleClose}
      />
    );
  }

  // Show the form
  return (
    <Stack gap={'xs'}>
      <Flex px={20} pt={16} direction={'column'}>
        <Title order={5} fw={600} mb={'xs'} c={'plum.9'}>
          Create Billable Schedule
        </Title>
        <Text size="xs" c={'plum'} mb={8}>
          Set Billable appointment for patient, which will automatically adjust appointments
          schedules.
        </Text>
        <Grid columns={6} mt={8}>
          <Grid.Col span={3}>
            <Text size="xs" c={'plum'} mb={4} fw={500}>
              Appointment Type
            </Text>
            <Dropdown
              options={[
                { label: 'Routine', value: 'routine' },
                { label: 'Heart Failure', value: 'heart failure' },
                { label: 'Combo', value: 'combo' }
              ]}
              value={
                appointmentType
                  ? { label: appointmentTypeLabel(appointmentType), value: appointmentType }
                  : undefined
              }
              onChange={(option) => {
                setAppointmentType(option?.value ?? null);
              }}
              isMulti={false}
              placeholder="Select Appointment Type"
              isDisabled={loading}
            />
          </Grid.Col>
          <Grid.Col span={3}>
            <Text size="xs" c={'plum'} mb={4} fw={500}>
              Scheduled Date
            </Text>
            <DatePicker
              inputProps={{
                placeholder: 'MM/DD/YYYY',
                required: true,
                disabled: loading
              }}
              datePickerProps={{
                selected: scheduledDate,
                onChange: setScheduledDate,
                isClearable: false,
                minDate: getTomorrow(),
                dateFormat: 'MM/dd/yyyy',
                disabled: loading
              }}
            />
          </Grid.Col>
        </Grid>
        {settingsError && (
          <Alert icon={<IconAlertCircle size="1rem" />} title="Error" color="red" mt={12}>
            {settingsError}
          </Alert>
        )}
      </Flex>
      <Group className={styles.buttonGroup} justify="flex-end">
        <Button
          variant="outline"
          color="plum.9"
          onClick={onBack}
          className={styles.modalButton}
          disabled={loading}>
          Back
        </Button>
        <Button
          color="plum.9"
          onClick={handleManualSave}
          className={styles.modalButton}
          disabled={
            !appointmentType || !scheduledDate || loading || !frequencyReady || frequencyFailed
          }
          loading={loading}>
          Save
        </Button>
      </Group>
    </Stack>
  );
};

export default BillableSchedule;
